// Allowlist des origines de shell autorisées à piloter le connecteur.
//
// Pourquoi ce module existe : le connecteur tourne dans l'app d'un TIERS et se
// laissait piloter par n'importe quel parent, l'origine du shell étant devinée
// depuis `document.referrer` (avec repli sur `"*"`). Un site capable d'embarquer
// l'app cible pouvait donc lui demander une capture d'écran, démarrer un
// enregistrement rrweb et lire le diagnostic — le referrer étant lui-même
// contrôlable par l'attaquant (`referrerpolicy="no-referrer"` forçait le repli).
//
// Le seul rempart restant était le `frame-ancestors` de l'app cible, hors de
// notre contrôle et impossible à poser sur les builders no-code que le produit
// vise (Lovable/Replit/Framer). D'où une allowlist embarquée dans le bundle.

/** Origines de la surface Livery (apps/lab) en déploiement Leesto. */
export const DEFAULT_SHELL_ORIGINS: readonly string[] = [
  "https://lab.leesto.app",
  "https://lab-dev.leesto.app",
];

/** Origines de dev du shell (cf. NEXT_PUBLIC_LIVE_APP_URL dans .env.example).
 *  Ajoutées UNIQUEMENT quand la page embarquée est elle-même en loopback. */
export const DEV_SHELL_ORIGINS: readonly string[] = [
  "http://localhost:3505",
  "http://127.0.0.1:3505",
];

/** Vrai pour une origine servie depuis la machine locale. */
export function isLoopbackOrigin(origin: string): boolean {
  try {
    const host = new URL(origin).hostname;
    return (
      host === "localhost" ||
      host.endsWith(".localhost") ||
      host === "127.0.0.1" ||
      host === "[::1]"
    );
  } catch {
    return false;
  }
}

/** Réduit une entrée déclarée à son origine ; `null` si inexploitable. */
function toOrigin(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const { origin } = new URL(trimmed);
    return origin === "null" ? null : origin;
  } catch {
    return null;
  }
}

/** Liste des origines de shell auxquelles ce connecteur accepte de parler.
 *  Une déclaration explicite (data-shell-origin / option) REMPLACE les défauts :
 *  un shell auto-hébergé n'a pas à faire confiance aux origines Leesto. */
export function resolveShellOrigins(input: {
  explicit?: readonly string[];
  pageOrigin: string;
}): string[] {
  const explicit = (input.explicit ?? []).map(toOrigin).filter((o): o is string => o !== null);
  if (explicit.length > 0) return [...new Set(explicit)];

  const defaults = [...DEFAULT_SHELL_ORIGINS];
  if (isLoopbackOrigin(input.pageOrigin)) defaults.push(...DEV_SHELL_ORIGINS);
  return [...new Set(defaults)];
}

/** Comparaison d'origine STRICTE (schéma + hôte + port), pour couper court aux
 *  sosies de domaine. `"*"`, `""` et `"null"` ne sont jamais des origines
 *  valides : une cible non résolue doit faire échouer la connexion, pas diffuser. */
export function isAllowedShellOrigin(origin: string, allowlist: readonly string[]): boolean {
  if (!origin || origin === "*" || origin === "null") return false;
  return allowlist.includes(origin);
}
